import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { getPlaylistMedia } from "../../lib/dashboardApi";
import type { MediaItem } from "../../types/dashboard";
import { MediaPreview } from "./MediaPreview";
import { Modal } from "./Modal";

// Gallery preview of a playlist's images/videos. Opened by clicking a playlist
// card on the advertiser detail page. Fetches the playlist's media on demand
// and lets the user click any tile to open the full-size lightbox.
export function PlaylistPreview({
  playlistId,
  playlistName,
  onClose,
}: {
  playlistId: number | null;
  playlistName?: string;
  onClose: () => void;
}) {
  const [lightbox, setLightbox] = useState<MediaItem | null>(null);
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["playlist-media", playlistId],
    queryFn: () => getPlaylistMedia(playlistId as number),
    enabled: playlistId != null,
  });

  function close() {
    setLightbox(null);
    onClose();
  }

  return (
    <>
      <Modal open={playlistId != null} onClose={close} width={720} ariaLabel="Playlist preview">
        <h3 style={{ marginBottom: 14 }}>{playlistName ?? "Playlist"}</h3>
        {isLoading ? (
          <div className="empty">Loading…</div>
        ) : items.length === 0 ? (
          <div className="empty">This playlist has no media yet.</div>
        ) : (
          <div className="adv-media-grid">
            {items.map((m) => (
              <div
                key={m.id}
                className="adv-media-item"
                onClick={() => setLightbox(m)}
              >
                {m.mime_type?.startsWith("video") ? (
                  <video src={m.url} muted />
                ) : (
                  <img src={m.url} alt={m.original_name} />
                )}
                <div className="adv-media-name">{m.original_name}</div>
              </div>
            ))}
          </div>
        )}
        <div className="modal-actions">
          <button type="button" className="btn btn-outline" onClick={close}>
            Close
          </button>
        </div>
      </Modal>
      <MediaPreview item={lightbox} onClose={() => setLightbox(null)} />
    </>
  );
}
